import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import {
    FaAmbulance,
    FaUserMd,
    FaClinicMedical,
    FaEnvelope,
    FaPhone,
} from "react-icons/fa";

export default function App() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const [activeSection, setActiveSection] = useState("hero");
    const NAV_HEIGHT = 72;

    const handleNavClick = useCallback((e, id) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.pageYOffset - NAV_HEIGHT;
        window.scrollTo({ top: y, behavior: "smooth" });
        setMenuOpen(false);
        setServicesOpen(false);
    }, []);

    // Scroll-Spy
    useEffect(() => {
        const sections = document.querySelectorAll("section");
        const handleScroll = () => {
            let current = "hero";
            sections.forEach((section) => {
                const sectionTop = section.offsetTop - NAV_HEIGHT - 50;
                if (window.scrollY >= sectionTop) {
                    current = section.getAttribute("id");
                }
            });
            setActiveSection(current);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const linkClass = (id) =>
        `hover:text-blue-600 ${activeSection === id ? "text-blue-600 font-semibold" : ""
        }`;

    return (
        <div className="font-sans">
            <style>{`
        html { scroll-behavior: smooth; }
        section { scroll-margin-top: ${NAV_HEIGHT + 8}px; }
      `}</style>

            {/* Navbar */}
            <nav className="fixed top-0 left-0 w-full z-50 bg-white/95 backdrop-blur border-b">
                <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
                    <a
                        href="#hero"
                        onClick={(e) => handleNavClick(e, "hero")}
                        className="text-xl font-bold text-blue-600"
                    >
                        LifeNext
                    </a>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex gap-8 text-gray-700 items-center">
                        <a
                            href="#about"
                            onClick={(e) => handleNavClick(e, "about")}
                            className={linkClass("about")}
                        >
                            About Us
                        </a>

                        {/* Services Menu */}
                        <div className="relative flex items-center gap-1">
                            {/* Services text (scrolls to section) */}
                            <a
                                href="#services"
                                onClick={(e) => handleNavClick(e, "services")}
                                className={linkClass("services")}
                            >
                                Services
                            </a>

                            {/* Arrow button (toggles dropdown) */}
                            <button
                                onClick={() => setServicesOpen(!servicesOpen)}
                                className="hover:text-blue-600"
                            >
                                ▼
                            </button>

                            {/* Dropdown - now positioned below the arrow */}
                            {servicesOpen && (
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white shadow-lg border rounded-lg z-50">
                                    <a
                                        href="#ambulance"
                                        onClick={(e) => handleNavClick(e, "ambulance")}
                                        className={linkClass("ambulance") + " block px-4 py-2 hover:bg-gray-100"}
                                    >
                                        Ambulance
                                    </a>
                                    <a
                                        href="#consultancy"
                                        onClick={(e) => handleNavClick(e, "consultancy")}
                                        className={linkClass("consultancy") + " block px-4 py-2 hover:bg-gray-100"}
                                    >
                                        Consultancy
                                    </a>
                                    <a
                                        href="#medicalcare"
                                        onClick={(e) => handleNavClick(e, "medicalcare")}
                                        className={linkClass("medicalcare") + " block px-4 py-2 hover:bg-gray-100"}
                                    >
                                        Medical Care
                                    </a>
                                </div>
                            )}
                        </div>


                        <a
                            href="#team"
                            onClick={(e) => handleNavClick(e, "team")}
                            className={linkClass("team")}
                        >
                            Team
                        </a>
                        <a
                            href="#contact"
                            onClick={(e) => handleNavClick(e, "contact")}
                            className={linkClass("contact")}
                        >
                            Contact
                        </a>
                    </div>

                    {/* Mobile menu button */}
                    <button
                        className="md:hidden text-2xl p-2"
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        ☰
                    </button>
                </div>

                {/* Mobile Dropdown */}
                {menuOpen && (
                    <div className="md:hidden bg-white border-t">
                        <a
                            href="#about"
                            onClick={(e) => handleNavClick(e, "about")}
                            className={linkClass("about") + " block px-4 py-2"}
                        >
                            About Us
                        </a>

                        <div className="flex items-center justify-between px-4 py-2 hover:bg-gray-100">
                            <a
                                href="#services"
                                onClick={(e) => handleNavClick(e, "services")}
                                className={linkClass("services")}
                            >
                                Services
                            </a>
                            <button
                                onClick={() => setServicesOpen(!servicesOpen)}
                                className="hover:text-blue-600"
                            >
                                ▼
                            </button>
                        </div>

                        {servicesOpen && (
                            <div className="pl-6">
                                <a
                                    href="#ambulance"
                                    onClick={(e) => handleNavClick(e, "ambulance")}
                                    className={linkClass("ambulance") + " block px-4 py-2"}
                                >
                                    Ambulance
                                </a>
                                <a
                                    href="#consultancy"
                                    onClick={(e) => handleNavClick(e, "consultancy")}
                                    className={linkClass("consultancy") + " block px-4 py-2"}
                                >
                                    Consultancy
                                </a>
                                <a
                                    href="#medicalcare"
                                    onClick={(e) => handleNavClick(e, "medicalcare")}
                                    className={linkClass("medicalcare") + " block px-4 py-2"}
                                >
                                    Medical Care
                                </a>
                            </div>
                        )}

                        <a
                            href="#team"
                            onClick={(e) => handleNavClick(e, "team")}
                            className={linkClass("team") + " block px-4 py-2"}
                        >
                            Team
                        </a>
                        <a
                            href="#contact"
                            onClick={(e) => handleNavClick(e, "contact")}
                            className={linkClass("contact") + " block px-4 py-2"}
                        >
                            Contact
                        </a>
                    </div>
                )}
            </nav>
            {/* Hero Section */}
            <section
                id="hero"
                className="min-h-screen flex flex-col justify-center items-center text-center bg-gradient-to-r from-blue-600 to-blue-400 text-white px-6"
            >
                <motion.h1
                    className="text-4xl md:text-6xl font-bold mb-4"
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    AI-Powered Emergency Assistance
                </motion.h1>
                <p className="max-w-2xl text-lg">
                    Smart assist during the golden hour of an emergency — because every
                    second counts.
                </p>
                <a
                    href="#services"
                    onClick={(e) => handleNavClick(e, "services")}
                    className="mt-6 inline-block bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-200"
                >
                    Get Started
                </a>
            </section>

            {/* About Us */}
            <section id="about" className="py-20 px-6 bg-gray-50">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-3xl font-bold mb-6 text-blue-600 text-center">
                        About Us
                    </h2>
                    <div className="text-gray-700 text-lg space-y-6 text-justify">
                        <p>
                            LifeNext Technology is an AI-powered first-aid and medical
                            emergency discovery platform. Our mission is to revolutionize the
                            way emergency medical services are delivered in India by
                            leveraging technology to save precious time during the golden
                            hours of a crisis.
                        </p>
                        <p>
                            Every year, millions of lives are lost because of delays in
                            accessing quality emergency services. More than 30% of deaths are
                            due to delayed ambulances, while nearly 55% of cardiac arrest
                            deaths are caused by a lack of timely intervention. India
                            continues to face an acute shortage of ambulances, doctors, and
                            essential first-aid knowledge — challenges that LifeNext is
                            committed to solving.
                        </p>
                        <p>
                            Our platform automates the entire emergency ecosystem: from
                            detecting and responding to an emergency, to guiding bystanders
                            with AI-powered first-aid instructions, to dispatching ambulances
                            and notifying hospitals in real-time. By doing so, we aim to
                            eliminate unnecessary delays and ensure patients receive
                            immediate, high-quality medical care.
                        </p>
                        <p>
                            LifeNext is designed not only to save lives but also to reduce the
                            stress and confusion that families experience during emergencies.
                            By bridging the gap between patients, ambulances, hospitals, and
                            doctors, we create a seamless, life-saving ecosystem. Our ultimate
                            vision is simple but powerful —{" "}
                            <strong>No life will be lost again in emergencies.</strong>
                        </p>
                    </div>
                </div>
            </section>

            {/* Services */}
            <section id="services" className="py-20 px-6">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl font-bold text-center text-blue-600 mb-10">
                        Our Services
                    </h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div
                            onClick={(e) => handleNavClick(e, "ambulance")}
                            className="shadow-lg p-6 text-center rounded-xl bg-white cursor-pointer hover:shadow-xl transition"
                        >
                            <FaAmbulance className="text-5xl mx-auto mb-4 text-red-500" />
                            <h3 className="text-xl font-semibold mb-2">Ambulance</h3>
                            <p>
                                Instant ambulance booking, hospital notifications, and AI-guided
                                first aid support.
                            </p>
                        </div>
                        <div
                            onClick={(e) => handleNavClick(e, "consultancy")}
                            className="shadow-lg p-6 text-center rounded-xl bg-white cursor-pointer hover:shadow-xl transition"
                        >
                            <FaUserMd className="text-5xl mx-auto mb-4 text-green-500" />
                            <h3 className="text-xl font-semibold mb-2">Consultancy</h3>
                            <p>
                                24/7 access to doctors and medical professionals during
                                emergencies.
                            </p>
                        </div>
                        <div
                            onClick={(e) => handleNavClick(e, "medicalcare")}
                            className="shadow-lg p-6 text-center rounded-xl bg-white cursor-pointer hover:shadow-xl transition"
                        >
                            <FaClinicMedical className="text-5xl mx-auto mb-4 text-blue-500" />
                            <h3 className="text-xl font-semibold mb-2">Medical Care</h3>
                            <p>
                                Connecting patients with nearby hospitals and ensuring quality
                                treatment without delays.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ambulance Booking Service */}
            <section id="ambulance" className="py-20 px-6 bg-gray-50">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl font-bold text-blue-600 mb-10 text-center">
                        Ambulance Booking Service
                    </h2>

                    <div className="grid md:grid-cols-2 gap-10 items-center">
                        <div className="flex justify-center">
                            <FaAmbulance className="text-red-500 text-[180px]" />
                        </div>
                        <div className="text-gray-700 space-y-6">
                            <p className="text-lg">
                                Our Ambulance Booking Service ensures that help is just a click
                                away. With real-time tracking, hospital notifications, and
                                AI-powered dispatching, we make sure every second counts during
                                an emergency.
                            </p>
                            <p className="text-lg">
                                From doorstep pick-up to instant hospital admission, our system
                                is designed to minimize delays and maximize survival chances.
                            </p>
                            <button
                                type="button"
                                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 shadow-lg"
                            >
                                🚑 Book Ambulance
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Consultancy Service */}
            <section id="consultancy" className="py-20 px-6">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl font-bold text-blue-600 mb-10 text-center">
                        Consultancy Service
                    </h2>

                    <div className="grid md:grid-cols-2 gap-10 items-center">
                        <div className="flex justify-center">
                            <FaUserMd className="text-green-500 text-[180px]" />
                        </div>
                        <div className="text-gray-700 space-y-6">
                            <p className="text-lg">
                                Our 24/7 Consultancy Service connects you instantly with
                                certified doctors during emergencies. You get immediate
                                first-aid guidance while a hospital team prepares in advance.
                            </p>
                            <p className="text-lg">
                                Whether it's trauma, cardiac arrest, or urgent care, our doctors
                                are just one click away.
                            </p>
                            <button
                                type="button"
                                className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 shadow-lg"
                            >
                                👨‍⚕️ Consult a Doctor
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Medical Care Service */}
            <section id="medicalcare" className="py-20 px-6 bg-gray-50">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl font-bold text-blue-600 mb-10 text-center">
                        Medical Care Service
                    </h2>

                    <div className="grid md:grid-cols-2 gap-10 items-center">
                        <div className="flex justify-center">
                            <FaClinicMedical className="text-blue-500 text-[180px]" />
                        </div>
                        <div className="text-gray-700 space-y-6">
                            <p className="text-lg">
                                Our Medical Care Service ensures seamless hospital admission and
                                immediate treatment without delays. Patients are transported
                                safely and hospitals are already prepared with critical
                                information.
                            </p>
                            <p className="text-lg">
                                By bridging patients, ambulances, and hospitals, we deliver a
                                smooth and effective care experience.
                            </p>
                            <button
                                type="button"
                                className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 shadow-lg"
                            >
                                🏥 Access Medical Care
                            </button>
                        </div>
                    </div>
                </div>
            </section>

        {/* Team */}
<section id="team" className="py-20 px-6 bg-gray-50">
  <div className="max-w-7xl mx-auto text-center">
    <h2 className="text-3xl font-bold text-blue-600 mb-12">Meet the Team</h2>

    <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        {/* Team Member 4 */}
      <div className="bg-white shadow-lg rounded-xl overflow-hidden hover:shadow-xl transition">
        <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
          <img
            src="/images/Sachin.jpeg"
            alt="Sachin Sharma"
            className="max-h-64 object-contain"
          />
        </div>
        <div className="p-6">
          <h3 className="text-xl font-semibold">Sachin Sharma</h3>
          <p className="text-gray-500 mb-3">CEO</p>
          <p className="text-gray-600 text-sm leading-relaxed">
            15+ years of experience in business development, sales and partnerships.
          </p>
        </div>
      </div>
      {/* Team Member 1 */}
      <div className="bg-white shadow-lg rounded-xl overflow-hidden hover:shadow-xl transition">
        <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
          <img
            src="/images/Suraj.jpeg"
            alt="Suraj Sharma"
            className="max-h-64 object-contain"
          />
        </div>
        <div className="p-6">
          <h3 className="text-xl font-semibold">Suraj Sharma</h3>
          <p className="text-gray-500 mb-3">CTO</p>
          <p className="text-gray-600 text-sm leading-relaxed">
           5+ years of experience in product development, front-end and back-end.
            Holds a B.Tech in Computer Science and Engineering.
          </p>
        </div>
      </div>

      {/* Team Member 2 */}
      <div className="bg-white shadow-lg rounded-xl overflow-hidden hover:shadow-xl transition">
        <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
          <img
            src="/images/Sunil.jpeg"
            alt="Sunil Dutt Sharma"
            className="max-h-64 object-contain"
          />
        </div>
        <div className="p-6">
          <h3 className="text-xl font-semibold">Sunil Dutt Sharma</h3>
          <p className="text-gray-500 mb-3">COO</p>
          <p className="text-gray-600 text-sm leading-relaxed">
            10+ years of experience in operation management and business startegy.
          </p>
        </div>
      </div>

      {/* Team Member 3 */}
      <div className="bg-white shadow-lg rounded-xl overflow-hidden hover:shadow-xl transition">
        <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
          <img
            src="/images/Prreyas.jpeg"
            alt="Prreyas Bharadwaj"
            className="max-h-64 object-contain"
          />
        </div>
        <div className="p-6">
          <h3 className="text-xl font-semibold">Prreyas Bharadwaj</h3>
          <p className="text-gray-500 mb-3">CMO</p>
          <p className="text-gray-600 text-sm leading-relaxed">
            5+ years of experience in marketing and branding.
          </p>
        </div>
      </div>

    </div>
  </div>
</section>




            {/* Contact */}
            <section id="contact" className="py-20 px-6 bg-gray-50">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-blue-600 mb-6">Contact Us</h2>
                    <p className="mb-6 text-gray-700">
                        Have questions? Reach out to us today.
                    </p>
                    <div className="flex flex-col md:flex-row justify-center gap-8">
                        {/* <div className="flex items-center gap-3">
                            <FaPhone className="text-2xl" />
                            <span>+91-XXXXXXXXXX</span>
                        </div> */}
                        <div className="flex items-center gap-3">
                            <FaEnvelope className="text-2xl" />
                            <span>lifenexttech@gmail.com</span>
                        </div>
                    </div>
                    <form className="mt-8 grid gap-4 max-w-md mx-auto">
                        <input
                            type="text"
                            placeholder="Your Name"
                            className="border rounded-lg p-3 w-full"
                        />
                        <input
                            type="number"
                            placeholder="Your Mobile Number"
                            className="border rounded-lg p-3 w-full"
                        />
                        <input
                            type="email"
                            placeholder="Your Email"
                            className="border rounded-lg p-3 w-full"
                        />
                        <textarea
                            placeholder="Your Message"
                            className="border rounded-lg p-3 w-full"
                            rows="4"
                        ></textarea>
                        <button
                            type="submit"
                            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 shadow-lg"
                        >
                            Send Message
                        </button>
                    </form>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-blue-600 text-white py-6 text-center">
                <p>© 2025 LifeNext. All rights reserved.</p>
            </footer>
        </div>
    );
}
